/**
 * testingModeApi — Bearer-only Central calls for the platform-internal
 * testing-mode panel.
 *
 * Uses shared path constants + normalizers from @levelup/central-auth.
 * No cookies. No proxy.
 */

import {
  TESTING_MODE_PATHS,
  normalizeTestingModeOptions,
  normalizeTestingModeStatus,
  type ApplyOverrideParams,
  type TestingModeOptions,
  type TestingModeStatus,
} from '@levelup/central-auth';
import { CENTRAL_URL } from '../config/centralAuth';
import { throwClassifiedCentralFailure } from '../auth/reportCentralFailure';

function authHeaders(token: string, extra: Record<string, string> = {}): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    ...extra,
  };
}

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    // THE choke point for every browser-direct Central call in this module. It
    // used to throw a plain Error, which the UI turned into generic "Failed to
    // load …" copy for a terminal 401 — with no login transition at all, because
    // nothing classified the answer.
    await throwClassifiedCentralFailure(res, 'testingModeApi');
  }
  return res.json();
}

export async function fetchTestingModeOptions(token: string): Promise<TestingModeOptions> {
  const res = await fetch(`${CENTRAL_URL}${TESTING_MODE_PATHS.options}`, {
    headers: authHeaders(token),
  });
  return normalizeTestingModeOptions(await readJson(res));
}

export async function fetchTestingModeStatus(token: string): Promise<TestingModeStatus> {
  const res = await fetch(`${CENTRAL_URL}${TESTING_MODE_PATHS.status}`, {
    headers: authHeaders(token),
  });
  return normalizeTestingModeStatus(await readJson(res));
}

export async function applyTestingModeOverride(
  token: string,
  params: ApplyOverrideParams,
): Promise<void> {
  const res = await fetch(`${CENTRAL_URL}${TESTING_MODE_PATHS.override}`, {
    method: 'POST',
    headers: authHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(params),
  });
  await readJson(res);
}

export async function clearTestingModeOverride(token: string): Promise<void> {
  const res = await fetch(`${CENTRAL_URL}${TESTING_MODE_PATHS.override}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  await readJson(res);
}
